import { useContext, useMemo } from "react"

import { IGatsbyImageData } from "gatsby-plugin-image"
import { useI18next, useTranslation } from "gatsby-plugin-react-i18next"
import styled from "styled-components"

import Carousel from "components/Carousels/Carousel"
import carouselData from "components/Carousels/CarouselData"
import MobileCarousel from "components/MobileCarousel"
import { ScreenContext } from "components/Providers"
import colors from "styles/colors"
import media from "styles/media"
import { checkedLocaleLink } from "utils/routes"
import useMedia from "utils/useMedia"

interface Props {
  allCarouselImages: Record<string, IGatsbyImageData | undefined>
}

export default function HomeCarousel({ allCarouselImages }: Props) {
  const { solutions } = carouselData
  const { mobile } = useContext(ScreenContext)
  const { t } = useTranslation()

  const {
    loansVal,
    loansValM,
    depositsVal,
    depositsMVal,
    cardVal,
    cardsMVal,
    riskVal,
    riskMVal,
    insightsVal,
    insightsMVal,
    readerVal,
    readerMVal,
  } = allCarouselImages

  const deposits = useMedia(depositsVal, depositsVal, depositsVal, depositsMVal)
  const cards = useMedia(cardVal, cardVal, cardVal, cardsMVal)
  const risk = useMedia(riskVal, riskVal, riskVal, riskMVal)
  const loans = useMedia(loansVal, loansVal, loansVal, loansValM)
  const insights = useMedia(insightsVal, insightsVal, insightsVal, insightsMVal)
  const reader = useMedia(readerVal, readerVal, readerVal, readerMVal)
  const { language } = useI18next()

  const buttonsArr = useMemo(
    () => [
      { buttonText: t("Cards") },
      { buttonText: t("Deposits") },
      { buttonText: t("Payments & Transfers") },
      { buttonText: t("Credit & Lending") },
      { buttonText: t("Data & Analytics") },
      { buttonText: t("Risk & Compliance") },
    ],
    [t]
  )

  const sections = useMemo(
    () => [
      {
        body: t(
          "Our in-house expertise and simplified integrations will set your card issuing process up for success from the start."
        ),
        link: {
          text: t("Explore Cards"),
          url:
            checkedLocaleLink("/products/card-issuing", language)
        },
        img: cards,
        alt: t("3 credit cards"),
      },
      {
        body: t(
          "Improve your customer experience with myriad deposit options like direct deposit, early pay, round-up and overdraft capabilities."
        ),
        link: {
          text: t("Explore deposits"),
          url:
            checkedLocaleLink("/products/deposits-and-banking", language)
        },
        img: deposits,
        alt: t("Insights and account ui"),
      },
      {
        body: t(
          "Seamless and secure transactional experiences are the baseline for payment processing and money transfers today."
        ),
        link: {
          text: t("Explore Payments & Transfers"),
          url:
            language === "en"
              ? checkedLocaleLink(
                  "/products/payment-processing-and-money-transfers",
                  language
                )
              : checkedLocaleLink(
                  "/blog/que-es-una-plataforma-de-procesamiento-de-emisores",
                  language
                ),
        },
        img: reader,
        alt: t("credit card and card reader"),
      },
      {
        body: t(
          "Whether it's delivering faster, innovative credit solutions—such as single-use virtual credit cards-or lending services such as personal loans, installment loans, or secured credit, Galileo has you covered."
        ),
        link: {
          text: t("Explore credit & lending"),
          url:
            (language === "en" || language === "pt")
              ? checkedLocaleLink("/products/credit-and-lending", language)
              : "/es/lp/informe-de-credito-garantizado/"
        },
        img: loans,
        alt: t("Payment details UI"),
      },
      {
        body: t(
          "Build customized financial reports and share business analytics across your organization to enhance visibility, analysis and decision-making capabilities that accelerate your growth."
        ),
        link: {
          text: t("Explore Data & Analytics"),
          url: checkedLocaleLink("/products/data-and-analytics", language),
        },
        img: insights,
        alt: t("Bar and pie graph"),
      },
      {
        body: t(
          "Galileo offers a suite of payment card fraud management solutions to secure all your transactions. We offer a wide range of services to mitigate program losses due to fraud while ensuring a frictionless transaction experience for your customers."
        ),
        link: {
          text: t("Explore Risk & Compliance"),
          url: checkedLocaleLink("/products/risk-and-compliance", language),
        },
        img: risk,
        alt: t("Bar graph and warning ui"),
      },
    ],
    [t, language, cards, deposits, reader, loans, insights, risk]
  )

  return mobile ? (
    <MobileCarousel
      gradient={{
        gradient: `${colors.magBlueHorz}`,
        fill: `${colors.blue400}`,
      }}
      sections={sections}
      buttonsArr={buttonsArr}
      calloutText={solutions}
    />
  ) : (
    <StyledCarousel
      gradient={{
        gradient: `${colors.magBlueHorz}`,
        fill: `${colors.blue400}`,
      }}
      className="homepage_carousel"
      sections={sections}
      buttonsArr={buttonsArr}
      calloutText={solutions}
    />
  )
}

const StyledCarousel = styled(Carousel)`
  ${media.fullWidth} {
    padding: 144px 0 180px;
  }
  ${media.desktop} {
    padding: 8.675vw 0 10.843vw;
  }
`
